import { apiClient } from '../client';

// Promise cache for deduplication
let authenticationPromise: Promise<any> | null = null;
let refreshUserDataPromise: Promise<any> | null = null;

export const signIn = async (): Promise<any> => {  
  try{
    const response = await apiClient.get('/account/user/sign-in', {
      params: { component_id: import.meta.env.VITE_PUBLIC_COMPONENT_ID },
    });
    // throw new Error("aaaa");
    return {
      data: response.data,
    };
  } catch (error: any) {
    if (error?.message == "aaaa"){
      return {
        errorCode: "SS-019",
        errorMessage: "No profile found",
      };
    }
    // in case of access denied or wrong credentials
    return {
      errorCode: error.response?.data?.exceptionResponse?.errorCode,
      errorMessage: error.response?.data?.exceptionResponse?.errorMessage || error.response?.data?.message,
    };
  }
};

/**
 * Helper function to update localStorage from authentication result
 * Centralizes all localStorage updates to prevent race conditions
 * 
 * NOTE: This is kept for backward compatibility with code that reads from localStorage.
 * The primary source of truth is Redux (persisted via redux-persist).
 * Consider migrating code to read from Redux instead of localStorage.
 */
export const updateLocalStorageFromAuthResult = (authResult: any): void => {
  try {
    console.log("💾 [AUTH] Updating localStorage (backup only - Redux is source of truth)");
    
    // Store permissions (backup - primary is in Redux)
    if (authResult.userInfo?.user_permissions) {
      localStorage.setItem(
        "permissions",
        JSON.stringify(
          authResult.userInfo.user_permissions.map((p: any) => p.name) || []
        )
      );
    }

    // Store tenant_id (backup - primary is in Redux)
    if (authResult.userInfo?.user_profile?.tenant?.id) {
      localStorage.setItem(
        "tenant_id",
        JSON.stringify(authResult.userInfo.user_profile.tenant.id)
      );
    }

    // Store user_groups (backup - primary is in Redux)
    if (authResult.userInfo?.user_info?.user_groups) {
      localStorage.setItem(
        "user_groups",
        JSON.stringify(authResult.userInfo.user_info.user_groups)
      );
    }

    // Store branches (backup - primary is in Redux)
    if (authResult.userInfo?.user_info?.user_branches) {
      localStorage.setItem(
        "branches",
        JSON.stringify(
          authResult.userInfo.user_info.user_branches.map(
            (branch: { id: number }) => branch.id
          ) || []
        )
      );
    }
    
    console.log("✅ [AUTH] localStorage backup updated");
  } catch (error) {
    console.error("❌ [AUTH] Error updating localStorage from auth result:", error);
  }
};

/**
 * Authenticate user with Keycloak tokens
 * Uses deduplication to prevent multiple concurrent calls
 */
export const authenticateUser = async (
  keycloakTokens: {
    access_token: string;
    id_token: string;
    refresh_token: string;
    expires_at: number;
  }
): Promise<any> => {
  console.log("🔐 [AUTH] authenticateUser called");
  console.log("🔐 [AUTH] Token present:", !!keycloakTokens.access_token);
  console.log("🔐 [AUTH] Token expires at:", new Date(keycloakTokens.expires_at * 1000).toISOString());
  
  // Return existing promise if authentication is already in progress
  if (authenticationPromise) {
    console.log("⚠️ [AUTH] Authentication already in progress, returning existing promise");
    return authenticationPromise;
  }

  // Create new authentication promise
  authenticationPromise = (async () => {
    try {
      console.log("🔐 [AUTH] Setting token in localStorage");
      // Set token in localStorage for API calls
      localStorage.setItem("token", keycloakTokens.access_token);
      
      console.log("🔐 [AUTH] Calling sign-in API endpoint");
      // Call sign-in API
      const userInfoResponse = await signIn();
      
      console.log("🔐 [AUTH] Sign-in API response received:", {
        hasError: !!userInfoResponse.errorCode,
        errorCode: userInfoResponse.errorCode,
        hasData: !!userInfoResponse.data
      });
      
      // Check for errors or missing data
      if (userInfoResponse.errorCode === "SS-019" || userInfoResponse.message?.toLowerCase()?.includes("no profile found")) {
        console.error("❌ [AUTH] Access denied - no profile found");
        return {
          hasAccess: false,
          errorMessageKey: "Access denied",
        };
      }
      
      // CRITICAL: Validate that we actually got user data
      if (!userInfoResponse.data || !userInfoResponse.data.user_info) {
        console.error("❌ [AUTH] Sign-in API returned no data - likely network/CORS error");
        console.error("❌ [AUTH] This usually means the API call failed but didn't throw an error");
        throw new Error("Sign-in API returned no user data");
      }
      
      const result = {
        hasAccess: true,
        userInfo: userInfoResponse.data,
      };

      console.log("✅ [AUTH] Authentication successful");
      console.log("🔐 [AUTH] User info:", {
        email: result.userInfo?.user_info?.email,
        subject_id: result.userInfo?.user_info?.subject_id,
        permissions: result.userInfo?.user_permissions?.length,
        branches: result.userInfo?.user_info?.user_branches?.length
      });

      // Update localStorage with auth result
      console.log("💾 [AUTH] Updating localStorage with auth result");
      updateLocalStorageFromAuthResult(result);
      console.log("✅ [AUTH] localStorage updated successfully");

      return result;
    } catch (error: any) {
      console.error("❌ [AUTH] Authentication error:", error);
      console.error("❌ [AUTH] Error details:", {
        message: error.message,
        errorCode: error.errorCode,
        response: error.response?.data
      });
      
      // Access denied
      if (error.errorCode === "SS-019" || error.message?.toLowerCase()?.includes("no profile found")) {
        console.error("❌ [AUTH] Access denied error");
        return {
          hasAccess: false,
          errorMessageKey: "Access denied",
        };
      }
      // Authentication failed
      console.error("❌ [AUTH] Authentication failed");
      return {
        hasAccess: false,
        errorMessageKey: 'shared.auth.authenticationFailed',
      };
    } finally {
      // Clear the promise cache after completion
      console.log("🔐 [AUTH] Clearing authentication promise cache");
      authenticationPromise = null;
    }
  })();

  return authenticationPromise;
};

/**
 * Refresh user data from the backend
 * Used when user data needs to be updated (e.g., after creating a branch)
 * Uses the same sign-in endpoint but with deduplication
 */
export const refreshUserData = async (): Promise<any> => {
  // Return existing promise if refresh is already in progress
  if (refreshUserDataPromise) {
    console.log("⚠️ User data refresh already in progress, returning existing promise");
    return refreshUserDataPromise;
  }

  // Create new refresh promise
  refreshUserDataPromise = (async () => {
    try {
      console.log("🔄 [AUTH] Refreshing user data");
      // Call sign-in API to get updated user data
      const userInfoResponse = await signIn();
      
      console.log("🔄 [AUTH] Refresh response received:", {
        hasError: !!userInfoResponse.errorCode,
        errorCode: userInfoResponse.errorCode,
        hasData: !!userInfoResponse.data
      });
      
      if (userInfoResponse.errorCode === "SS-019" || userInfoResponse.message?.toLowerCase()?.includes("no profile found")) {
        console.error("❌ [AUTH] Refresh failed - no profile found");
        return {
          hasAccess: false,
          errorMessageKey: "Access denied",
        };
      }
      
      // CRITICAL: Validate that we actually got user data
      if (!userInfoResponse.data || !userInfoResponse.data.user_info) {
        console.error("❌ [AUTH] Refresh returned no data - likely network/CORS error");
        throw new Error("Refresh API returned no user data");
      }
      
      const result = {
        hasAccess: true,
        userInfo: userInfoResponse.data,
      };

      console.log("✅ [AUTH] User data refreshed successfully");
      // Update localStorage with refreshed data
      updateLocalStorageFromAuthResult(result);

      return result;
    } catch (error: any) {
      console.error("Error refreshing user data:", error);
      return {
        hasAccess: false,
        errorMessageKey: 'shared.auth.refreshFailed',
        error: error.message,
      };
    } finally {
      // Clear the promise cache after completion
      refreshUserDataPromise = null;
    }
  })();

  return refreshUserDataPromise;
}

  export const handleAuthenticationError = (
    store: any,
    logout: any
  ): void => {
    store.dispatch(logout());
    localStorage.removeItem("token");
    sessionStorage.clear();
    window.location.href = "/login";
  };

  // update the last login time
  export const updateLastLoginTime = async (): Promise<any> => {
    try {
      await apiClient.post('/account/v3/user/login');
    } catch (error: any) {
      return {
        errorCode: error.response?.data?.exceptionResponse?.errorCode,
        errorMessage: error.response?.data?.exceptionResponse?.errorMessage || error.response?.data?.message,
      };
    }
  };